/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/** A purchase form owns its variant and allocation. Other purchase surfaces receive immutable snapshots. */
window.hydromateProduct = function (config) {
  const initial = config.variants.find((variant) => String(variant.id) === String(config.selectedId)) || config.variants[0];
  return {
    _form: null, _variantListener: null, _masterListener: null, _stateListener: null,
    formId: config.formId,
    selected: String(initial.id), variantId: String(initial.id), available: initial.available,
    subscription: false, sellingPlanId: '', productPrice: initial.productPrice,
    sellingPlanProductPrice: initial.productPrice, priceInt: initial.priceInt, priceCents: initial.priceCents,
    sellingPlanPriceInt: initial.priceInt, sellingPlanPriceCents: initial.priceCents,
    customPrice: initial.customPrice, sellingPlanCustomPrice: initial.customPrice,
    oneTimeSavings: initial.oneTimeSavings, subscriptionSavings: initial.subscriptionSavings,
    discountPercent: 0, unit: initial.unit, comparePrice: initial.comparePrice, onSale: initial.onSale,
    savePrice: initial.savePrice, savePercent: initial.savePercent,
    init() {
      this._form = this.$el.querySelector('form[is="product-form"]');
      if (!this._form) return;
      this._variantListener = (event) => {
        if (event.detail.variant) this.applyVariant(event.detail.variant.id);
      };
      this._form.addEventListener('variant:changed', this._variantListener);
      this._masterListener = () => this.applyVariant(this._form.elements.namedItem('id')?.value);
      this._form.elements.namedItem('id')?.addEventListener('change', this._masterListener);
      this.$watch('subscription', () => this.sync());
      this.$watch('sellingPlanId', () => this.sync());
      this.applyVariant(this._form.elements.namedItem('id')?.value || this.variantId);
    },
    destroy() {
      this._form?.removeEventListener('variant:changed', this._variantListener);
      this._form?.elements.namedItem('id')?.removeEventListener('change', this._masterListener);
      if (this._form) delete this._form.hydromateState;
    },
    applyVariant(id) {
      const variant = config.variants.find((item) => String(item.id) === String(id));
      if (!variant) return;
      this.variantId = this.selected = String(variant.id);
      ['available', 'productPrice', 'priceInt', 'priceCents', 'customPrice', 'unit', 'oneTimeSavings', 'subscriptionSavings', 'comparePrice', 'onSale', 'savePrice', 'savePercent'].forEach((key) => this[key] = variant[key]);
      const plan = variant.plans.find((item) => String(item.id) === String(this.sellingPlanId)) || variant.plans[0];
      this.sellingPlanId = plan ? String(plan.id) : '';
      this.sellingPlanProductPrice = plan ? plan.productPrice : variant.productPrice;
      this.sellingPlanPriceInt = plan ? plan.priceInt : variant.priceInt;
      this.sellingPlanPriceCents = plan ? plan.priceCents : variant.priceCents;
      this.sellingPlanCustomPrice = plan ? plan.customPrice : variant.customPrice;
      if (!plan) this.subscription = false;
      this.sync();
    },
    selectVariant(id) {
      const master = this._form?.elements.namedItem('id');
      if (!master || !config.variants.some((item) => String(item.id) === String(id))) return;
      master.value = String(id);
      this.applyVariant(id);
      master.dispatchEvent(new Event('change', { bubbles: true }));
    },
    optionLabel(id) {
      const variant = config.variants.find((item) => String(item.id) === String(id));
      if (!variant) return '';
      const plan = variant.plans.find((item) => String(item.id) === String(this.sellingPlanId)) || variant.plans[0];
      const perItem = this.subscription && plan ? plan.customPrice : variant.customPrice;
      return variant.title + (perItem ? ` — ${perItem}/${variant.unit}` : '');
    },
    setSubscription(value) {
      this.subscription = Boolean(value && this.sellingPlanId);
      this.sync();
    },
    sync() {
      const variant = config.variants.find((item) => String(item.id) === this.variantId);
      const plan = variant?.plans.find((item) => String(item.id) === String(this.sellingPlanId));
      if (this.subscription && !plan) this.subscription = false;
      if (plan) {
        this.discountPercent = variant.price > 0 ? Math.round((variant.price - plan.price) * 100 / variant.price) : 0;
        const firstPlan = variant.plans[0];
        const saving = /^\$(\d+)(?:\.(\d{1,2}))?$/.exec(String(variant.subscriptionSavings || ''));
        if (plan.price === firstPlan.price) this.subscriptionSavings = variant.subscriptionSavings;
        else if (saving) {
          const cents = Number(saving[1]) * 100 + Number((saving[2] || '').padEnd(2, '0'));
          this.subscriptionSavings = `$${(Math.max(0, cents + firstPlan.price - plan.price) / 100).toFixed(2)}`;
        } else this.subscriptionSavings = '';
        this.sellingPlanProductPrice = plan.productPrice;
        this.sellingPlanPriceInt = plan.priceInt;
        this.sellingPlanPriceCents = plan.priceCents;
        this.sellingPlanCustomPrice = plan.customPrice;
      }
      if (!plan) this.discountPercent = 0;
      const state = {};
      ['formId', 'variantId', 'available', 'subscription', 'sellingPlanId', 'productPrice', 'sellingPlanProductPrice', 'priceInt', 'priceCents', 'sellingPlanPriceInt', 'sellingPlanPriceCents', 'customPrice', 'sellingPlanCustomPrice', 'unit', 'comparePrice', 'onSale', 'savePrice', 'savePercent'].forEach((key) => state[key] = this[key]);
      if (!this._form) return;
      this._form.hydromateState = state;
      this._form.dispatchEvent(new CustomEvent('hydromate:product-state', { bubbles: true, detail: state }));
    }
  };
};

window.hydromateProductMirror = function (config) {
  const state = window.hydromateProduct(config);
  return Object.assign(state, {
    init() {
      this._stateListener = (event) => {
        if (event.detail.formId === this.formId) Object.assign(this, event.detail);
      };
      document.addEventListener('hydromate:product-state', this._stateListener);
      const form = document.getElementById(this.formId);
      if (form?.hydromateState) Object.assign(this, form.hydromateState);
    },
    destroy() { document.removeEventListener('hydromate:product-state', this._stateListener); }
  });
};

document.addEventListener('hydromate:product-state', function (event) {
  const state = event.detail;
  document.querySelectorAll('[data-product-state-for]').forEach(function (line) {
    if (line.dataset.productStateFor !== state.formId) return;
    const price = line.querySelector('[data-cro-price]');
    const compare = line.querySelector('[data-cro-compare]');
    const savings = line.querySelector('[data-cro="save-chip"]');
    if (price) price.textContent = state.subscription ? state.sellingPlanProductPrice : state.productPrice;
    if (compare) {
      compare.textContent = state.comparePrice;
      compare.hidden = state.subscription || !state.onSale;
    }
    if (savings) {
      savings.hidden = state.subscription || !state.onSale;
      savings.textContent = state.onSale ? `You save ${state.savePrice} · ${state.savePercent}% off` : '';
    }
  });
});

// Unblock the theme runtime after the product factories are installed.
document.dispatchEvent(new Event('hydromate:product-factories-ready'));
